import { useEffect, useMemo, useState } from "react";
import { Icon, Radio } from "@thetreep/ui";
import { useTranslation } from "react-i18next";
import {
  Offer,
  Price,
  Flexibility,
  Product,
  ProductBySegmentsAndPassengers,
  TrainCabinClass,
  PlaneCabinClass,
  Transportation,
} from "@gen/travel/v1/common_pb";
import i18n from "@libs/i18n";
import { getEnumName } from "@utils/enums";
import { useBookingStore } from "@stores/useBookingStore";
import { Solution } from "@gen/travel/v1/search_pb";
import { Tag } from "@thetreep/ui";
import theme from "@theme";
import { useDAP } from "@pages/booking/dap.hook";
import { ExchangeOffer, ExchangeSolution } from "@gen/travel/v1/exchange_pb";

export interface OfferChoicesProps {
  solution: Solution | ExchangeSolution;
  offers: (Offer | ExchangeOffer)[];
  selectedOffer: Offer | ExchangeOffer;
  setSelectedOffer: (offer: Offer | ExchangeOffer) => void;
}

export function OfferChoices({
  solution,
  offers,
  selectedOffer,
  setSelectedOffer,
}: OfferChoicesProps) {
  const { t } = useTranslation();
  const { searchRequest } = useBookingStore();
  const { dap } = useDAP();
  const nbPassengers = (searchRequest?.additionalPassengers?.length ?? 0) + 1;

  useEffect(() => {
    if (!selectedOffer || !offers.find((o) => o.id === selectedOffer.id)) {
      if (offers.length > 0) {
        setSelectedOffer(offers[0]);
      }
    }
  }, [offers, selectedOffer]);

  function formatPrice(price: Price | undefined): string {
    if (!price) {
      return t("results.unavailable");
    }
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: price.currency,
      minimumFractionDigits: 2,
    }).format(Number(price.amount) / 100);
  }
  return (
    <div className="flex flex-col gap-6">
      {offers.map((offer, index) => (
        <div
          key={index}
          data-testid="offer"
          className="border border-light-blue-200 rounded-lg p-4 flex flex-col gap-4 w-full"
        >
          <div className="flex flex-col gap-4">
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setSelectedOffer(offer)}
            >
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-center gap-2">
                  <Radio checked={selectedOffer?.id === offer.id} />
                  <span>
                    {nbPassengers}{" "}
                    {t("booking.ticket", { count: nbPassengers })}{" "}
                    {t(
                      "results." + getEnumName(Flexibility, offer.flexibility),
                    )}
                  </span>
                </div>
                {!dap.isComplianceFilterEnabled && offer.isCompliant && (
                  <Tag
                    size="small"
                    bg={theme.colors.secondary.soft}
                    color={theme.colors.zinc[800]}
                    text={t("conform")}
                  />
                )}
                {!dap.isComplianceFilterEnabled && !offer.isCompliant && (
                  <Tag
                    size="small"
                    bg={theme.colors.negative.soft}
                    color={theme.colors.neutral[50]}
                    text={t("notConform")}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              {offer.products
                .filter((p) => !!p)
                .map((p) => (
                  <ProductItem
                    key={`${p.product?.code || ""}-${p.segmentIds.join("-")}`}
                    solution={solution}
                    product={p}
                  />
                ))}
            </div>

            <div className="flex flex-col text-sm text-text-strong gap-2">
              {offer.$typeName === "travel.v1.Offer" ? (
                <>
                  <div className="flex justify-between">
                    <p>{t("results.initialPrice")}:</p>
                    <p>{formatPrice(offer.initialPrice)}</p>
                  </div>
                  <div className="flex justify-between">
                    <p>{t("results.coveredAmount")}:</p>
                    <p>{formatPrice(offer.subsidyAmount)}</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <p>{t("results.priceDifference")}:</p>
                    <p>{formatPrice(offer.priceDelta)}</p>
                  </div>
                  <div className="flex justify-between">
                    <p>{t("results.exchangeFees")}:</p>
                    <p>{formatPrice(offer.exchangeFees)}</p>
                  </div>
                </>
              )}
              <div className="rounded flex items-center justify-between bg-light-blue-100 p-4">
                <p className="text-base">{t("outOfPocketExpense")}:</p>
                <p className="font-bold font-jakarta text-[20px]">
                  {formatPrice(offer.outOfPocketAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductItem({
  solution,
  product: productBySegmentsAndPassengers,
}: {
  solution: Solution | ExchangeSolution;
  product: ProductBySegmentsAndPassengers;
}) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const product = productBySegmentsAndPassengers.product;

  function productLabel(product: Product) {
    let label = "";
    segments?.forEach((s) => {
      if (s.transport?.number) {
        label += `${s.transport?.name} N° ${s.transport?.number} - `;
      } else {
        label += `${s.transport?.name} - `;
      }
    });

    label += `1 ${t("booking.ticket", { count: 1 })}`;

    const transportType = segments?.[0]?.transport?.type;

    if (transportType === Transportation.TRAIN) {
      switch (product.cabinClass?.cabinClass.value) {
        case TrainCabinClass.SECOND:
          label += ` - ${t("results.secondClass")}`;
          break;
        case TrainCabinClass.FIRST:
          label += ` - ${t("results.firstClass")}`;
          break;
        default:
          break;
      }
    } else if (transportType === Transportation.PLANE) {
      switch (product.cabinClass?.cabinClass.value) {
        case PlaneCabinClass.ECONOMY:
          label += ` - ${t("results.economy")}`;
          break;
        case PlaneCabinClass.ECONOMY_PREMIUM:
          label += ` - ${t("results.economyPremium")}`;
          break;
        case PlaneCabinClass.BUSINESS:
          label += ` - ${t("results.business")}`;
          break;
        default:
          break;
      }
    }

    switch (product.flexibility) {
      case Flexibility.NO_FLEX:
        label += ` ${t("results.NO_FLEX")}`;
        break;
      case Flexibility.SEMI_FLEX:
        label += ` ${t("results.SEMI_FLEX")}`;
        break;
      case Flexibility.FULL_FLEX:
        label += ` ${t("results.FULL_FLEX")}`;
        break;
      default:
        break;
    }

    if (product.accommodation?.seat) {
      label += ` - ${t("results.coach")} ${product.accommodation?.coach} ${t("results.seat")} ${product.accommodation?.seat}`;
    }

    return label;
  }

  const segments = useMemo(() => {
    return solution.journey?.segments.filter((s) =>
      productBySegmentsAndPassengers.segmentIds.includes(s.id),
    );
  }, [solution, product]);

  if (!product) return;

  return (
    <div>
      <div className="flex flex-col justify-start items-start gap-2 w-full">
        <div className="flex gap-2 items-center whitespace-normal">
          <span className="font-bold text-text-strong text-sm">
            {product.code.toUpperCase()}
          </span>
          {product?.isForDisabledPassenger && (
            <div className="flex items-center justify-center h-4 w-4 rounded-full bg-cnam-bright">
              <Icon src="PMR" color="white" size={10} />
            </div>
          )}
        </div>
        <div
          data-testid="offer-fare-toggle"
          className="flex items-center cursor-pointer truncate"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="mr-2 text-blue-600 text-sm truncate">
            {t("results.fareConditions")}
          </div>
          <div
            className={`transform transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          >
            <Icon src="down_line" color="#3079F2" />
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="mt-2 p-2 rounded-lg bg-cnam-light-100">
          <div
            data-testid="offer-product"
            className="border-b last:border-0 p-2 text-text"
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                className="font-bold text-xs"
                data-testid="offer-product-heading"
              >
                {productLabel(product)}
              </span>
            </div>
            <div
              data-testid="offer-product-conditions"
              className="text-xs"
              dangerouslySetInnerHTML={{
                __html: product?.conditions[0].description || "",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
